import React, { useEffect, useState, createElement } from 'react';
import { Download, FileSpreadsheet, Calendar, Building2, MapPin, AlertCircle, Search } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { useOrders } from '../contexts/OrderContext';
import { getCompanies } from '../utils/companyManagement';
export function ReportsPage() {
  const {
    orders
  } = useOrders();
  const [selectedCompany, setSelectedCompany] = useState('Todas');
  const [selectedBranch, setSelectedBranch] = useState('Todas');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateError, setDateError] = useState('');
  // Applied filters - start showing ALL orders by default
  const [appliedFilters, setAppliedFilters] = useState({
    company: 'Todas',
    branch: 'Todas',
    dateFrom: '',
    dateTo: ''
  });
  const companies = getCompanies();
  const companyNames = ['Todas', ...companies.map(c => c.name)];
  const branches = selectedCompany === 'Todas' ? ['Todas'] : ['Todas', ...(companies.find(c => c.name === selectedCompany)?.branches.map(b => b.name) || [])];
  // Debug: Log orders on mount
  useEffect(() => {
    console.log('📊 ReportsPage - Total orders:', orders.length);
    console.log('📊 ReportsPage - Orders:', orders);
  }, [orders]);
  // Validate date range
  const validateDates = (from: string, to: string) => {
    if (!from || !to) {
      setDateError('');
      return true;
    }
    const fromDate = new Date(from);
    const toDate = new Date(to);
    if (fromDate > toDate) {
      setDateError('La fecha "Desde" no puede ser posterior a la fecha "Hasta"');
      return false;
    }
    setDateError('');
    return true;
  };
  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    validateDates(value, dateTo);
  };
  const handleDateToChange = (value: string) => {
    setDateTo(value);
    validateDates(dateFrom, value);
  };
  const handleSearch = () => {
    if (dateError) {
      return;
    }
    console.log('🔍 Searching with filters:', {
      company: selectedCompany,
      branch: selectedBranch,
      dateFrom,
      dateTo
    });
    setAppliedFilters({
      company: selectedCompany,
      branch: selectedBranch,
      dateFrom: dateFrom,
      dateTo: dateTo
    });
  };
  const filteredOrders = orders.filter(order => {
    // Don't filter cancelled orders
    if (order.status === 'Cancelado') {
      console.log('❌ Filtered out cancelled order:', order.orderNo);
      return false;
    }
    // Apply company filter
    if (appliedFilters.company !== 'Todas' && order.company !== appliedFilters.company) {
      console.log('❌ Filtered out by company:', order.orderNo, order.company);
      return false;
    }
    // Apply branch filter
    if (appliedFilters.branch !== 'Todas' && order.branch !== appliedFilters.branch) {
      console.log('❌ Filtered out by branch:', order.orderNo, order.branch);
      return false;
    }
    // Apply date filtering ONLY if dates are provided
    if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      try {
        // Parse order date from Spanish format "DD/MM/YYYY HH:MM:SS AM/PM" or "DD/MM/YYYY, HH:MM:SS AM/PM"
        console.log('📅 Parsing order date:', order.orderDate);
        // Remove comma if present and split
        const cleanDate = order.orderDate.replace(',', '');
        const dateTimeParts = cleanDate.split(' ');
        const datePart = dateTimeParts[0]; // "DD/MM/YYYY"
        const parts = datePart.split('/');
        if (parts.length !== 3) {
          console.warn('⚠️ Invalid date format:', order.orderDate);
          return true;
        }
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        console.log('📅 Parsed date components:', {
          day,
          month,
          year
        });
        // Create date string in YYYY-MM-DD format for comparison (no timezone issues)
        const orderDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        console.log('📅 Order date string:', orderDateStr);
        if (appliedFilters.dateFrom) {
          console.log('📅 From date string:', appliedFilters.dateFrom);
          console.log('📅 Comparing orderDateStr >= fromDate:', orderDateStr >= appliedFilters.dateFrom);
          if (orderDateStr < appliedFilters.dateFrom) {
            console.log('❌ Filtered out by dateFrom:', order.orderNo);
            return false;
          }
        }
        if (appliedFilters.dateTo) {
          console.log('📅 To date string:', appliedFilters.dateTo);
          console.log('📅 Comparing orderDateStr <= toDate:', orderDateStr <= appliedFilters.dateTo);
          if (orderDateStr > appliedFilters.dateTo) {
            console.log('❌ Filtered out by dateTo:', order.orderNo, 'orderDate:', orderDateStr, 'toDate:', appliedFilters.dateTo);
            return false;
          }
        }
      } catch (e) {
        console.error('❌ Error parsing date:', order.orderDate, e);
        return true;
      }
    }
    console.log('✅ Order passed all filters:', order.orderNo);
    return true;
  });
  console.log('📊 Filtered orders count:', filteredOrders.length);
  // Calculate totals
  const totalSales = filteredOrders.reduce((sum, order) => sum + parseFloat(order.total), 0);
  const totalDiscount = filteredOrders.reduce((sum, order) => sum + parseFloat(order.discount || '0'), 0);
  const totalSubsidy = filteredOrders.reduce((sum, order) => sum + parseFloat(order.subsidyAmount || '0'), 0);
  const exemptSales = filteredOrders.filter(o => o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
  const taxableSales = filteredOrders.filter(o => !o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
  const subtotalExempt = exemptSales;
  const subtotalTaxable = taxableSales / 1.18;
  const subtotal = subtotalExempt + subtotalTaxable;
  const tax = taxableSales - subtotalTaxable;
  const totalOrders = filteredOrders.length;
  const averageOrder = totalOrders > 0 ? totalSales / totalOrders : 0;
  console.log('💰 Totals:', {
    totalOrders,
    totalSales,
    totalSubsidy,
    subtotal,
    tax
  });
  const salesByCompany = companies.map(company => {
    const companyOrders = filteredOrders.filter(o => o.company === company.name);
    const total = companyOrders.reduce((sum, o) => sum + parseFloat(o.total), 0);
    const exemptTotal = companyOrders.filter(o => o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
    const taxableTotal = companyOrders.filter(o => !o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
    return {
      company: company.name,
      orders: companyOrders.length,
      total: total,
      subtotal: exemptTotal + taxableTotal / 1.18,
      tax: taxableTotal - taxableTotal / 1.18,
      exempt: exemptTotal,
      taxable: taxableTotal
    };
  }).filter(s => s.orders > 0);
  const salesByBranch = filteredOrders.reduce((acc, order) => {
    const key = `${order.company} - ${order.branch}`;
    if (!acc[key]) {
      acc[key] = {
        company: order.company,
        branch: order.branch,
        orders: 0,
        total: 0,
        subtotal: 0,
        tax: 0,
        exempt: 0,
        taxable: 0
      };
    }
    const orderTotal = parseFloat(order.total);
    acc[key].orders++;
    acc[key].total += orderTotal;
    if (order.taxExempt) {
      acc[key].exempt += orderTotal;
      acc[key].subtotal += orderTotal;
    } else {
      acc[key].taxable += orderTotal;
      acc[key].subtotal += orderTotal / 1.18;
      acc[key].tax += orderTotal - orderTotal / 1.18;
    }
    return acc;
  }, {} as Record<string, any>);
  const branchSales = Object.values(salesByBranch);
  const exportToExcel = () => {
    if (dateError) {
      alert('⚠️ Corrige el rango de fechas antes de exportar');
      return;
    }
    let csv = 'Reporte de Ventas - La Cochera\n\n';
    csv += `Fecha de Generación:,${new Date().toLocaleString('es-DO')}\n`;
    csv += `Empresa Filtrada:,${appliedFilters.company}\n`;
    csv += `Sucursal Filtrada:,${appliedFilters.branch}\n`;
    csv += `Rango de Fechas:,${appliedFilters.dateFrom || 'Todas'} - ${appliedFilters.dateTo || 'Todas'}\n\n`;
    csv += 'Resumen General\n';
    csv += `Total de Pedidos:,${totalOrders}\n`;
    csv += `Subtotal (sin ITBIS):,RD$ ${subtotal.toFixed(2)}\n`;
    csv += `ITBIS (18%):,RD$ ${tax.toFixed(2)}\n`;
    csv += `Descuentos:,RD$ ${totalDiscount.toFixed(2)}\n`;
    csv += `Subsidios:,RD$ ${totalSubsidy.toFixed(2)}\n`;
    csv += `Total Exento:,RD$ ${exemptSales.toFixed(2)}\n`;
    csv += `Total Gravado:,RD$ ${taxableSales.toFixed(2)}\n`;
    csv += `Total de Ventas:,RD$ ${totalSales.toFixed(2)}\n`;
    csv += `Promedio por Pedido:,RD$ ${averageOrder.toFixed(2)}\n\n`;
    csv += 'Ventas por Empresa\n';
    csv += 'Empresa,Pedidos,Subtotal,ITBIS,Exento,Gravado,Total\n';
    salesByCompany.forEach(s => {
      csv += `${s.company},${s.orders},RD$ ${s.subtotal.toFixed(2)},RD$ ${s.tax.toFixed(2)},RD$ ${s.exempt.toFixed(2)},RD$ ${s.taxable.toFixed(2)},RD$ ${s.total.toFixed(2)}\n`;
    });
    csv += '\nVentas por Sucursal\n';
    csv += 'Empresa,Sucursal,Pedidos,Subtotal,ITBIS,Exento,Gravado,Total\n';
    branchSales.forEach(s => {
      csv += `${s.company},${s.branch},${s.orders},RD$ ${s.subtotal.toFixed(2)},RD$ ${s.tax.toFixed(2)},RD$ ${s.exempt.toFixed(2)},RD$ ${s.taxable.toFixed(2)},RD$ ${s.total.toFixed(2)}\n`;
    });
    csv += '\nDetalle de Pedidos\n';
    csv += 'No. Orden,Fecha,Cliente,Empresa,Sucursal,Producto,Cantidad,Subtotal,ITBIS,Descuento,Subsidio,Exento,Total,Estado\n';
    filteredOrders.forEach(order => {
      const orderTotal = parseFloat(order.total);
      const orderSubtotal = order.taxExempt ? orderTotal : orderTotal / 1.18;
      const orderTax = order.taxExempt ? 0 : orderTotal - orderTotal / 1.18;
      const orderSubsidy = parseFloat(order.subsidyAmount || '0');
      csv += `${order.orderNo},${order.orderDate},${order.name},${order.company},${order.branch},"${order.product}",${order.quantity},RD$ ${orderSubtotal.toFixed(2)},RD$ ${orderTax.toFixed(2)},RD$ ${order.discount || '0.00'},RD$ ${orderSubsidy.toFixed(2)},${order.taxExempt ? 'Sí' : 'No'},RD$ ${order.total},${order.status}\n`;
    });
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte-ventas-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-2391">
      <div className="mb-8" data-id="element-2392">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2393">
          Reportes de Ventas
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-2394">
          Analiza y exporta datos de ventas por empresa y sucursal
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2" data-id="element-2395">
          Debug: {orders.length} órdenes totales | {filteredOrders.length}{' '}
          órdenes filtradas
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6" data-id="element-2396">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4" data-id="element-2397">
          Filtros
        </h3>

        {dateError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2" data-id="element-2398">
            <AlertCircle className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" size={18} data-id="element-2399" />
            <div data-id="element-2400">
              <p className="text-sm font-medium text-red-900 dark:text-red-100" data-id="element-2401">
                {dateError}
              </p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1" data-id="element-2402">
                Verifica que la fecha inicial sea anterior a la fecha final
              </p>
            </div>
          </div>}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-2403">
          <div data-id="element-2404">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2405">
              Empresa
            </label>
            <FilterDropdown label="" value={selectedCompany} options={companyNames} onChange={setSelectedCompany} data-id="element-2406" />
          </div>
          <div data-id="element-2407">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2408">
              Sucursal
            </label>
            <FilterDropdown label="" value={selectedBranch} options={branches} onChange={setSelectedBranch} data-id="element-2409" />
          </div>
          <div data-id="element-2410">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2411">
              Desde
            </label>
            <input type="date" value={dateFrom} onChange={e => handleDateFromChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2412" />
          </div>
          <div data-id="element-2413">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2414">
              Hasta
            </label>
            <input type="date" value={dateTo} onChange={e => handleDateToChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2415" />
          </div>
        </div>

        <div className="flex justify-end" data-id="element-2416">
          <Button onClick={handleSearch} leftIcon={<Search size={18} data-id="element-2418" />} disabled={!!dateError} data-id="element-2417">
            Buscar
          </Button>
        </div>
      </Card>

      {/* Summary Cards - ALWAYS SHOW */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6" data-id="element-2419">
        <Card data-id="element-2420">
          <div className="flex items-center gap-3 mb-2" data-id="element-2421">
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-2422">
              <FileSpreadsheet className="w-5 h-5 text-gray-700 dark:text-gray-300" data-id="element-2423" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2424">
              Total Pedidos
            </p>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-2425">
            {totalOrders}
          </p>
        </Card>

        <Card data-id="element-2426">
          <div className="flex items-center gap-3 mb-2" data-id="element-2427">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg" data-id="element-2428">
              <Download className="w-5 h-5 text-blue-700 dark:text-blue-400" data-id="element-2429" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2430">Subtotal</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2431">
            RD$ {subtotal.toFixed(2)}
          </p>
        </Card>

        <Card data-id="element-2432">
          <div className="flex items-center gap-3 mb-2" data-id="element-2433">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg" data-id="element-2434">
              <Calendar className="w-5 h-5 text-purple-700 dark:text-purple-400" data-id="element-2435" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2436">
              ITBIS (18%)
            </p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2437">
            RD$ {tax.toFixed(2)}
          </p>
        </Card>

        <Card data-id="element-2438">
          <div className="flex items-center gap-3 mb-2" data-id="element-2439">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg" data-id="element-2440">
              <Download className="w-5 h-5 text-green-700 dark:text-green-400" data-id="element-2441" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2442">
              Subsidios
            </p>
          </div>
          <p className="text-2xl font-bold text-green-900 dark:text-green-400" data-id="element-2443">
            RD$ {totalSubsidy.toFixed(2)}
          </p>
        </Card>

        <Card data-id="element-2444">
          <div className="flex items-center gap-3 mb-2" data-id="element-2445">
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-2446">
              <Download className="w-5 h-5 text-gray-700 dark:text-gray-300" data-id="element-2447" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2448">
              Total Ventas
            </p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2449">
            RD$ {totalSales.toFixed(2)}
          </p>
        </Card>
      </div>

      {/* Tax Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-2450">
        <Card className="bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800" data-id="element-2451">
          <div className="flex items-center justify-between" data-id="element-2452">
            <div data-id="element-2453">
              <p className="text-sm text-green-600 dark:text-green-400 font-medium mb-1" data-id="element-2454">
                Total Exento de ITBIS
              </p>
              <p className="text-3xl font-bold text-green-900 dark:text-green-300" data-id="element-2455">
                RD$ {exemptSales.toFixed(2)}
              </p>
            </div>
            <div className="text-green-600 dark:text-green-400" data-id="element-2456">
              <Building2 size={32} data-id="element-2457" />
            </div>
          </div>
        </Card>

        <Card className="bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800" data-id="element-2458">
          <div className="flex items-center justify-between" data-id="element-2459">
            <div data-id="element-2460">
              <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1" data-id="element-2461">
                Total Gravado con ITBIS
              </p>
              <p className="text-3xl font-bold text-blue-900 dark:text-blue-300" data-id="element-2462">
                RD$ {taxableSales.toFixed(2)}
              </p>
            </div>
            <div className="text-blue-600 dark:text-blue-400" data-id="element-2463">
              <Calendar size={32} data-id="element-2464" />
            </div>
          </div>
        </Card>
      </div>

      {/* Sales by Company */}
      <Card className="mb-6" data-id="element-2465">
        <div className="flex items-center justify-between mb-4" data-id="element-2466">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2467">
            Ventas por Empresa
          </h3>
          <Building2 className="w-5 h-5 text-gray-400" data-id="element-2468" />
        </div>
        <div className="space-y-3" data-id="element-2469">
          {salesByCompany.map((sale, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-2470">
              <div data-id="element-2471">
                <p className="font-medium text-gray-900 dark:text-white" data-id="element-2472">
                  {sale.company}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2473">
                  {sale.orders} pedidos
                </p>
              </div>
              <div className="text-right" data-id="element-2474">
                <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2475">
                  RD$ {sale.total.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2476">
                  ITBIS: RD$ {sale.tax.toFixed(2)} | Exento: RD${' '}
                  {sale.exempt.toFixed(2)}
                </p>
              </div>
            </div>)}
          {salesByCompany.length === 0 && <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-id="element-2477">
              No hay datos para mostrar con los filtros aplicados
            </p>}
        </div>
      </Card>

      {/* Sales by Branch */}
      <Card className="mb-6" data-id="element-2478">
        <div className="flex items-center justify-between mb-4" data-id="element-2479">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2480">
            Ventas por Sucursal
          </h3>
          <MapPin className="w-5 h-5 text-gray-400" data-id="element-2481" />
        </div>
        <div className="overflow-x-auto" data-id="element-2482">
          <table className="w-full text-left" data-id="element-2483">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-2484">
              <tr data-id="element-2485">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2486">
                  Empresa
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-2487">
                  Sucursal
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-2488">
                  Pedidos
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-2489">
                  Subtotal
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-2490">
                  ITBIS
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-2491">
                  Exento
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-2492">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2493">
              {branchSales.map((sale, index) => <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800" data-id="element-2494">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white" data-id="element-2495">
                    {sale.company}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-2496">
                    {sale.branch}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right" data-id="element-2497">
                    {sale.orders}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right" data-id="element-2498">
                    RD$ {sale.subtotal.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 text-right" data-id="element-2499">
                    RD$ {sale.tax.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-green-600 dark:text-green-400 text-right" data-id="element-2500">
                    RD$ {sale.exempt.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right" data-id="element-2501">
                    RD$ {sale.total.toFixed(2)}
                  </td>
                </tr>)}
              {branchSales.length === 0 && <tr data-id="element-2502">
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400" data-id="element-2503">
                    No hay datos para mostrar con los filtros aplicados
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Export Button */}
      <div className="flex justify-end" data-id="element-2504">
        <Button onClick={exportToExcel} leftIcon={<Download size={18} data-id="element-2506" />} size="lg" disabled={filteredOrders.length === 0 || !!dateError} data-id="element-2505">
          Exportar a Excel (CSV)
        </Button>
      </div>
    </div>;
}